import { useId, useRef, useState } from 'react';
import {
  buildIdentityPrompt,
  MODEL_VIEW_KINDS,
  type Build,
  type FashionModel,
  type Gender,
  type ModelAppearance,
  type ModelView,
  type ModelViewKind,
  type ModelWithStats,
} from '@studio/shared';

import { api, assetUrl } from '../lib/api';
import { downscaleImage } from '../lib/downscale';
import { choicesFor } from '../lib/appearance';
import { inspectImage, type CaptureIssue } from '../lib/capture';
import { CaptureNotes } from '../components/CaptureNotes';
import { KeepIcon, PlusIcon, RejectIcon, SearchIcon, SparkIcon, TrashIcon } from '../components/Icons';
import { Sheet } from '../components/Sheet';
import { Display } from '../components/Display';
import { Skeleton } from '../components/Skeleton';
import { useToast } from '../components/Toaster';
import { useI18n, useT, type Translate } from '../lib/i18n';
import { useErrorText } from '../lib/errors';
import { useLoad } from '../lib/useLoad';

const EMPTY: ModelAppearance = {
  gender: 'female',
  ageRange: 'mid 20s',
  ethnicity: '',
  skinTone: '',
  hairColor: '',
  hairLength: '',
  hairStyle: '',
  eyeColor: '',
  build: 'slim',
  height: '',
  makeup: 'natural-looking makeup',
  distinguishingFeatures: '',
};

const GENDERS: Gender[] = ['female', 'male', 'non-binary'];
const BUILDS: Build[] = ['slim', 'athletic', 'straight', 'curvy', 'plus', 'muscular'];

/** How the model came to be, in the line an agency would print. */
const PROVENANCE: Record<FashionModel['source'], string> = {
  manual: 'talent.source.manual',
  'ai-designed': 'talent.source.designed',
  uploaded: 'talent.source.uploaded',
};

export function ModelsPage() {
  const toast = useToast();
  const describe = useErrorText();
  const t = useT();
  const [editing, setEditing] = useState<ModelWithStats | 'new' | undefined>();
  const [find, setFind] = useState('');
  /*
   * A face is castable when it has a portrait attached; without one every
   * render invents the person again and the same model is a different woman
   * on each frame. So "has a face on file" is the only division of this list
   * worth offering, and it is the one that decides whether a shoot will hold
   * together.
   */
  const [only, setOnly] = useState<'all' | 'ready' | 'blank'>('all');

  // A refused list (signed out, suspended) is answered on the page by the
  // banner above it; the toast is for a failure that has no banner.
  const {
    data: models,
    loading,
    reload: refresh,
  } = useLoad(() => api.listModels(), {
    initial: [] as ModelWithStats[],
    onError: (err) => toast(describe(err, 'talent.loadFailed'), 'error'),
  });

  const needle = fold(find.trim());
  const shown = models.filter((model) => {
    if (only === 'ready' && model.referenceAssetIds.length === 0) return false;
    if (only === 'blank' && model.referenceAssetIds.length > 0) return false;
    return !needle || fold(model.name).includes(needle);
  });
  const ready = models.filter((model) => model.referenceAssetIds.length > 0).length;

  return (
    <div className="app__inner">
      <header className="pagehead">
        {/* The casting, and how many are in it, in the page's largest type. */}
        <Display
          text={`${t('talent.display')} ${String(models.length).padStart(2, '0')}`}
          mark="none"
          className="casting__title"
        />
        <button className="btn btn--primary" onClick={() => setEditing('new')}>
          <PlusIcon /> {t('talent.new')}
        </button>
      </header>

      {loading ? (
        <Skeleton count={3} ratio="4:5" />
      ) : models.length === 0 ? (
        <section className="feature">
          <div className="feature__top">
            <span className="feature__chip">{t('talent.eyebrow')}</span>
          </div>
          <div className="feature__text">
            <h2 className="feature__title">{t('talent.empty')}</h2>
            <p className="feature__lede">{t('talent.emptyHint')}</p>
            <div style={{ marginTop: 14 }}>
              <button className="btn btn--primary" onClick={() => setEditing('new')}>
                <SparkIcon /> {t('talent.emptyCta')}
              </button>
            </div>
          </div>
        </section>
      ) : (
        <>
          {models.length > 3 && (
            <div className="board-bar">
              {models.length > 5 && (
                <label className="finder">
                  <SearchIcon />
                  <input
                    className="finder__field"
                    value={find}
                    placeholder={t('talent.find')}
                    aria-label={t('talent.find')}
                    onChange={(event) => setFind(event.target.value)}
                  />
                </label>
              )}
              <div className="row row--wrap">
                {(['all', 'ready', 'blank'] as const).map((id) => (
                  <button
                    key={id}
                    type="button"
                    className={`chip ${only === id ? 'is-on' : ''}`}
                    onClick={() => setOnly(id)}
                  >
                    {t(`talent.only.${id}`)}
                    {id === 'ready' && <b>{ready}</b>}
                    {id === 'blank' && <b>{models.length - ready}</b>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {shown.length === 0 ? (
            <p className="muted" style={{ padding: '24px 4px' }}>
              {t('talent.noneMatch')}
            </p>
          ) : (
            <div className="books">
              {shown.map((model, index) => (
                <CompCard
                  key={model.id}
                  model={model}
                  t={t}
                  delay={60 + index * 50}
                  // The Studio's models are shown to everyone and edited here by nobody:
                  // they are managed in the admin area's catalogue.
                  onOpen={model.house ? undefined : () => setEditing(model)}
                />
              ))}
              {/* The way to a new face sits in the board, as its last card. */}
              <button
                type="button"
                className="castnew"
                style={{ animationDelay: `${60 + shown.length * 50}ms` }}
                onClick={() => setEditing('new')}
              >
                <span className="castnew__plus" aria-hidden="true">
                  +
                </span>
                <span>
                  <span className="castnew__title">{t('talent.newCard')}</span>
                  <span className="castnew__hint">{t('talent.newCardHint')}</span>
                </span>
              </button>
            </div>
          )}
        </>
      )}

      {editing && (
        <ModelEditor
          model={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(undefined)}
          onSaved={(message) => {
            setEditing(undefined);
            refresh();
            toast(message, 'ok');
          }}
          onChanged={refresh}
          toast={toast}
          t={t}
        />
      )}
    </div>
  );
}

/** Case- and accent-insensitive, so "eloise" finds "Éloïse". */
const fold = (text: string): string =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

/* ------------------------------------------------------------------ *
 * The comp card
 * ------------------------------------------------------------------ */

/**
 * Agencies represent a model with a comp card: one strong headshot and
 * supporting looks beside it. Adopting the real artifact means the supporting
 * slots have an obvious tenant — past frames of that model — so the card fills
 * itself in as you shoot. Here it is a grey slab with the name set as large as
 * a cover line, because a casting board is looked through by face and name.
 */
function CompCard({
  model,
  onOpen,
  t,
  delay,
}: {
  model: ModelWithStats;
  /** Absent for a model of the Studio's catalogue, which is not edited from here. */
  onOpen?: () => void;
  t: Translate;
  delay: number;
}) {
  const { stats } = model;
  const portrait = model.referenceAssetIds[0];

  // Supporting pictures: real frames first, then any spare reference angles.
  const looks = [...stats.recentAssetIds, ...model.referenceAssetIds.slice(1)].slice(0, 2);

  const coverage = model.referenceAssetIds.length;
  const health =
    coverage === 0
      ? { tone: 'bad', label: t('talent.health.bad'), hint: t('talent.health.badHint') }
      : coverage === 1
        ? { tone: 'warn', label: t('talent.health.warn'), hint: t('talent.health.warnHint') }
        : {
            tone: 'ok',
            label: t('talent.health.ok'),
            hint: t('talent.health.okHint', { n: coverage }),
          };

  return (
    <article className="castcard" style={{ animationDelay: `${delay}ms` }}>
      <button
        type="button"
        className={`comp${onOpen ? '' : ' comp--fixed'}`}
        onClick={onOpen}
        disabled={!onOpen}
        aria-label={onOpen ? t('talent.edit', { name: model.name }) : model.name}
      >
        {/*
         * Three pictures, the way an agency lays out a card: the face large
         * on the left, two of her at work beside it. The two fill themselves
         * in as she is shot.
         */}
        <span className="comp__mosaic">
          <span className="comp__portrait">
            {portrait ? (
              <img src={assetUrl(portrait)} alt={model.name} loading="lazy" />
            ) : (
              <span className="comp__blank">
                <span className="comp__initial">{model.name.slice(0, 1).toUpperCase()}</span>
              </span>
            )}
          </span>
          {[0, 1].map((i) => {
            const assetId = looks[i];
            return assetId ? (
              <img key={assetId} className="comp__look" src={assetUrl(assetId)} alt="" loading="lazy" />
            ) : (
              <span key={i} className="comp__slot" />
            );
          })}
          {model.candidateAssetIds.length > 0 && (
            <span className="comp__pending">
              {t(model.candidateAssetIds.length === 1 ? 'talent.pendingOne' : 'talent.pending', {
                n: model.candidateAssetIds.length,
              })}
            </span>
          )}
        </span>

        <span className="comp__name">{model.name}</span>
        <span className="comp__meta">
          {model.house ? (
            <span className="comp__studio">{t('wiz.studio')}</span>
          ) : (
            <span className="comp__agency">{t(PROVENANCE[model.source])}</span>
          )}
          {/*
           * Whether the face is pinned down, as one chip. The board is for
           * finding someone; the numbers behind this live in the editor, which
           * is where anything can be done about them.
           */}
          <span className={`comp__health is-${health.tone}`} title={health.hint}>
            <span className="comp__healthDot" />
            {health.label}
            {/*
             * The critic's mean identity score, and only where it means
             * something: beside "No face on file" a bare number would read as
             * a score of nothing.
             */}
            {coverage > 0 && stats.faceConsistency !== undefined && (
              <b title={t('talent.consistency')}>{stats.faceConsistency}%</b>
            )}
          </span>
        </span>
      </button>
    </article>
  );
}

/**
 * Casting a model, in one compact sheet.
 *
 * The face and her turnaround lead — four pictures in a row: the portrait,
 * then front, profile and back — because a model is how she looks, and one
 * press draws all four. Under them the name, a one-line brief for a new face,
 * and the description folded away: it is filled by the brief or read from an
 * imported photograph, and opened only to correct it. The identity line the
 * renderer receives sits in a fold of its own, for the rare hand edit.
 */
export function ModelEditor({
  model,
  onClose,
  onSaved,
  onChanged,
  toast,
  t,
  manual = false,
}: {
  model?: FashionModel;
  onClose: () => void;
  onSaved: (message: string) => void;
  onChanged: () => void;
  toast: (message: string, tone?: 'info' | 'ok' | 'error') => void;
  t: Translate;
  /**
   * Made from photographs alone: her face and her turnaround are uploaded, one
   * slot each, and nothing is drawn, written or read for her — no brief, no
   * portrait, no 360°, no description from the photograph. The Studio's
   * catalogue is cast this way.
   */
  manual?: boolean;
}) {
  const editing = Boolean(model);
  const describe = useErrorText();
  const { locale } = useI18n();

  const [name, setName] = useState(model?.name ?? '');
  const [appearance, setAppearance] = useState<ModelAppearance>(model?.appearance ?? EMPTY);
  const [identityPrompt, setIdentityPrompt] = useState(model?.identityPrompt ?? '');
  const [identityTouched, setIdentityTouched] = useState(Boolean(model));
  const [referenceAssetIds, setReferenceAssetIds] = useState<string[]>(model?.referenceAssetIds ?? []);
  const [candidates, setCandidates] = useState<string[]>(model?.candidateAssetIds ?? []);
  /** Her turnaround: full-body front, profile and back. */
  const [views, setViews] = useState<ModelView[]>(model?.views ?? []);
  /** Which side is being drawn again on its own, if one is. */
  const [redrawing, setRedrawing] = useState<ModelViewKind | undefined>();
  const [brief, setBrief] = useState('');
  const [designing, setDesigning] = useState(false);
  /** Where the one-press portrait and turnaround have got to. */
  const [step, setStep] = useState<'portrait' | 'views' | undefined>();
  const [takesBusy, setTakesBusy] = useState(false);
  const [reading, setReading] = useState(false);
  /** Manual: which photograph slot is being filled, and the picker that fills it. */
  const [filling, setFilling] = useState<'face' | ModelViewKind | undefined>();
  const slotRef = useRef<HTMLInputElement>(null);
  const slot = useRef<'face' | ModelViewKind>('face');
  /*
   * Not remembered between models on purpose. It is a claim about one
   * person, and a box that stays ticked is a claim nobody made.
   */
  const [consent, setConsent] = useState(false);
  const [notes, setNotes] = useState<Array<{ name: string; issues: CaptureIssue[] }>>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  // The identity line is what actually reaches the renderer, so it tracks the
  // form until the user edits it by hand.
  const derived = buildIdentityPrompt(appearance);
  const effectiveIdentity = identityTouched ? identityPrompt : derived;

  /**
   * A portrait is only worth rendering once the line says something. Straight
   * from the empty form it reads "a woman with natural-looking makeup", which
   * returns an arbitrary stranger — and a different one on every press.
   */
  const identityIsThin =
    !appearance.ethnicity.trim() &&
    !appearance.hairColor.trim() &&
    !appearance.distinguishingFeatures.trim() &&
    !identityTouched;

  const busy = Boolean(step) || designing || takesBusy || reading || Boolean(filling);

  const changed = editing
    ? name !== model!.name ||
      effectiveIdentity !== model!.identityPrompt ||
      Object.entries(appearance).some(
        ([key, value]) => value !== model!.appearance[key as keyof ModelAppearance],
      )
    : Boolean(name.trim()) ||
      Boolean(brief.trim()) ||
      identityTouched ||
      referenceAssetIds.length > 0 ||
      views.length > 0 ||
      Object.entries(appearance).some(([key, value]) => value !== EMPTY[key as keyof ModelAppearance]);

  const set = <K extends keyof ModelAppearance>(key: K, value: ModelAppearance[K]) =>
    setAppearance((prev) => ({ ...prev, [key]: value }));

  /**
   * The portrait and her turnaround, in one press: the face first (unless one
   * is already on file), then front, profile and back drawn from it. A saved
   * model files the turnaround at once; a new one keeps it until it is saved.
   */
  const shoot360 = async (identity = effectiveIdentity, kinds: ModelViewKind[] = [...MODEL_VIEW_KINDS]) => {
    setError('');
    try {
      let refs = referenceAssetIds;
      // Made by hand, her face is a photograph, never a render.
      if (!refs.length && manual) return;
      if (!refs.length) {
        setStep('portrait');
        const asset = await api.generatePortrait(identity, []);
        refs = [asset.id];
        setReferenceAssetIds(refs);
        if (model) await api.updateModel(model.id, { referenceAssetIds: refs });
      }
      setStep('views');
      if (model) {
        const result = await api.generateViews(model.id, kinds);
        setViews(result.model.views ?? []);
        onChanged();
        if (result.error) setError(result.error);
      } else {
        const result = await api.drawViews(identity, refs, kinds);
        setViews((prev) => [...prev.filter((v) => !kinds.includes(v.kind)), ...result.views]);
        if (result.error) setError(result.error);
      }
    } catch (err) {
      setError(describe(err, 'caster.errNoPortrait'));
    } finally {
      setStep(undefined);
    }
  };

  /** One side again, from the same face. */
  const redrawSide = async (kind: ModelViewKind) => {
    setRedrawing(kind);
    setError('');
    try {
      if (model) {
        const result = await api.generateViews(model.id, [kind]);
        setViews(result.model.views ?? []);
        onChanged();
      } else {
        const result = await api.drawViews(effectiveIdentity, referenceAssetIds, [kind]);
        setViews((prev) => [...prev.filter((v) => v.kind !== kind), ...result.views]);
      }
    } catch (err) {
      setError(describe(err, 'caster.errViews'));
    } finally {
      setRedrawing(undefined);
    }
  };

  const design = async () => {
    setDesigning(true);
    setError('');
    try {
      const designed = await api.designModel(brief);
      const { name: designedName, identityPrompt: designedIdentity, ...rest } = designed;
      setAppearance(rest);
      setName((current) => current || designedName);
      setIdentityPrompt(designedIdentity);
      setIdentityTouched(true);
      setDesigning(false);
      // A designed model arrives with a face and a turnaround, not just a paragraph.
      await shoot360(designedIdentity);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('caster.errNoDesign'));
      setDesigning(false);
    }
  };

  /**
   * Fresh takes of a saved model's face, filed as candidates beside her: the
   * face the shoots use never changes until one of them is kept.
   */
  const shootTakes = async () => {
    if (!model) return;
    setTakesBusy(true);
    setError('');
    try {
      const result = await api.shootPortraits(model.id, 3);
      setCandidates(result.model.candidateAssetIds);
      onChanged();
      if (result.error) {
        setError(t('caster.errPartial', { done: result.assetIds.length, total: 3, message: result.error }));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('caster.errNoPortrait'));
    } finally {
      setTakesBusy(false);
    }
  };

  const keepTake = async (assetId: string) => {
    if (!model) return;
    try {
      const updated = await api.keepPortrait(model.id, assetId);
      setCandidates(updated.candidateAssetIds);
      setReferenceAssetIds(updated.referenceAssetIds);
      onChanged();
      toast(t('caster.cast'), 'ok');
    } catch (err) {
      toast(err instanceof Error ? err.message : t('caster.errNoKeep'), 'error');
    }
  };

  const discardTake = async (assetId: string) => {
    if (!model) {
      setReferenceAssetIds((prev) => prev.filter((id) => id !== assetId));
      return;
    }
    try {
      const updated = await api.discardPortrait(model.id, assetId);
      setCandidates(updated.candidateAssetIds);
      setReferenceAssetIds(updated.referenceAssetIds);
      onChanged();
    } catch (err) {
      toast(err instanceof Error ? err.message : t('caster.errNoDiscard'), 'error');
    }
  };

  /** Promotes an existing reference to primary — the face shown everywhere. */
  const makePrimary = async (assetId: string) => {
    const next = [assetId, ...referenceAssetIds.filter((id) => id !== assetId)];
    setReferenceAssetIds(next);
    if (model) {
      try {
        await api.updateModel(model.id, { referenceAssetIds: next });
        onChanged();
      } catch (err) {
        toast(err instanceof Error ? err.message : t('caster.errNoReorder'), 'error');
      }
    }
  };

  const uploadReferences = async (files: FileList | null) => {
    if (!files?.length) return;
    setReading(true);
    setError('');
    try {
      /*
       * The same light-meter check the wardrobe does, and it matters more
       * here: a soft or badly lit face is read once into the identity
       * description and then carried into every frame of every roll.
       */
      const looked = await Promise.all(Array.from(files).map((file) => inspectImage(file)));
      setNotes(
        Array.from(files)
          .map((file, i) => ({ name: file.name, issues: looked[i]?.issues ?? [] }))
          .filter((note) => note.issues.length > 0),
      );

      const uploaded = await Promise.all(
        Array.from(files).map(async (file) =>
          api.uploadAsset(await downscaleImage(file), 'model-reference', { consent: true }),
        ),
      );
      const ids = uploaded.map((a) => a.id);
      // Your photo leads: it goes first, so it is the face everything matches.
      const next = [...ids, ...referenceAssetIds];
      setReferenceAssetIds(next);
      if (model) await api.updateModel(model.id, { referenceAssetIds: next });

      /*
       * Read the face and write the description from it. Otherwise the words
       * and the photograph are authored separately, and any disagreement
       * between them drags the render away from the face you uploaded.
       */
      const first = ids[0];
      if (first) {
        const read = await api.describeModel(first);
        const { name: readName, identityPrompt: readIdentity, ...rest } = read;
        setAppearance(rest);
        setName((current) => current || readName || '');
        setIdentityPrompt(readIdentity);
        setIdentityTouched(true);
      }
      onChanged();
    } catch (err) {
      // A refusal — an unclear face, a missing attestation — is the server
      // saying something specific; it deserves its own sentence.
      setError(describe(err, 'caster.errUpload'));
    } finally {
      setReading(false);
    }
  };

  /*
   * Manual: one photograph into one slot — her face, or a side of her
   * turnaround — replacing what was there. Uploaded as a likeness, under the
   * attestation, and nothing else: no reading, no drawing.
   */
  const pickFor = (which: 'face' | ModelViewKind) => {
    slot.current = which;
    slotRef.current?.click();
  };
  const fillSlot = async (file: File | undefined) => {
    if (slotRef.current) slotRef.current.value = '';
    if (!file) return;
    const which = slot.current;
    setFilling(which);
    setError('');
    try {
      const reading = await inspectImage(file);
      setNotes(reading?.issues.length ? [{ name: file.name, issues: reading.issues }] : []);
      const asset = await api.uploadAsset(await downscaleImage(file), 'model-reference', {
        consent: true,
        ...(which === 'face' ? {} : { view: which }),
      });
      if (which === 'face') {
        const old = referenceAssetIds[0];
        const next = [asset.id, ...referenceAssetIds.filter((id) => id !== old)];
        if (model && old) await api.discardPortrait(model.id, old);
        setReferenceAssetIds(next);
        if (model) await api.updateModel(model.id, { referenceAssetIds: next });
      } else {
        const next = [...views.filter((v) => v.kind !== which), { kind: which, assetId: asset.id }];
        setViews(next);
        if (model) await api.updateModel(model.id, { views: next });
      }
      if (model) onChanged();
    } catch (err) {
      setError(describe(err, 'caster.errUpload'));
    } finally {
      setFilling(undefined);
    }
  };

  /** Writes the turnaround now on a saved model; an unsaved one keeps it until it is saved. */
  const removeView = async (kind: ModelViewKind) => {
    const next = views.filter((v) => v.kind !== kind);
    setViews(next);
    if (!model) return;
    try {
      await api.updateModel(model.id, { views: next });
      onChanged();
    } catch (err) {
      toast(describe(err, 'caster.errNoSave'), 'error');
    }
  };

  const save = async () => {
    if (!name.trim()) {
      setError(t('caster.needName'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (model) {
        await api.updateModel(model.id, {
          name: name.trim(),
          appearance,
          identityPrompt: effectiveIdentity,
          referenceAssetIds,
        });
        onSaved(t('caster.saved', { name: name.trim() }));
      } else {
        await api.createModel({
          name: name.trim(),
          appearance,
          identityPrompt: effectiveIdentity,
          referenceAssetIds,
          views,
          source: manual ? 'uploaded' : identityTouched && brief ? 'ai-designed' : 'manual',
        });
        onSaved(t('caster.added', { name: name.trim() }));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('caster.errNoSave'));
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!model) return;
    setSaving(true);
    try {
      await api.deleteModel(model.id);
      onSaved(t('caster.removed', { name: model.name }));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('caster.errNoDelete'));
      setSaving(false);
    }
  };

  const face = referenceAssetIds[0];
  const whole = views.length === MODEL_VIEW_KINDS.length;
  /** Made by hand: the sides with no photograph yet, or all of them again once each has one. */
  const toDraw = whole
    ? [...MODEL_VIEW_KINDS]
    : MODEL_VIEW_KINDS.filter((k) => !views.some((v) => v.kind === k));

  return (
    <Sheet
      title={editing ? model!.name : t('caster.newTitle')}
      onClose={onClose}
      dirty={changed && !saving}
      footer={
        <div className="row" style={{ gap: 10 }}>
          {editing && (
            <button className="btn btn--danger btn--sm" disabled={saving} onClick={() => void remove()}>
              <TrashIcon /> {t('common.delete')}
            </button>
          )}
          <span className="faint grow">
            {!name.trim() ? t('caster.needName') : manual && !face ? t('caster.needFace') : ''}
          </span>
          <button
            className="btn btn--primary"
            disabled={saving || busy || !name.trim() || (manual && !face) || (editing && !changed)}
            onClick={() => void save()}
          >
            {t(saving ? 'caster.saving' : editing ? 'caster.saveEdit' : 'caster.saveNew')}
          </button>
        </div>
      }
    >
      <div className="mx">
        {error && <div className="banner banner--error">{error}</div>}

        {/* A new face starts from a sentence: it fills the form and shoots her 360°. */}
        {!editing && !manual && (
          <div className="mx__brief">
            <label className="sr-only" htmlFor="model-brief">
              {t('caster.brief')}
            </label>
            <input
              id="model-brief"
              className="input"
              value={brief}
              placeholder={t('caster.briefPlaceholder')}
              onChange={(event) => setBrief(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && brief.trim() && !busy) void design();
              }}
            />
            <button
              className="btn btn--primary"
              disabled={busy || !brief.trim()}
              onClick={() => void design()}
            >
              {designing ? <span className="spinner" /> : <SparkIcon />}
              {t(designing ? 'caster.designing' : 'caster.go')}
            </button>
          </div>
        )}

        {/* The face and her turnaround, side by side. */}
        <div className="mx__row" aria-label={t('caster.turnaround')}>
          <figure className="mx__shot mx__shot--face">
            <span className="mx__frame">
              {manual ? (
                <Slot
                  image={face}
                  label={t('caster.view.face')}
                  filling={filling === 'face'}
                  locked={!consent}
                  busy={busy}
                  t={t}
                  onPick={() => pickFor('face')}
                  onRemove={() => void discardTake(face!)}
                />
              ) : face ? (
                <img src={assetUrl(face)} alt="" />
              ) : step === 'portrait' ? (
                <span className="spinner" />
              ) : (
                <span className="mx__initial">
                  {name.trim() ? name.trim().slice(0, 1).toUpperCase() : ''}
                </span>
              )}
            </span>
            <figcaption>{t('caster.view.face')}</figcaption>
          </figure>
          {MODEL_VIEW_KINDS.map((kind) => {
            const view = views.find((v) => v.kind === kind);
            const side = t(`caster.view.${kind}`);
            const drawing = (step === 'views' && !view) || redrawing === kind;
            return (
              <figure key={kind} className="mx__shot">
                <span className="mx__frame">
                  {manual ? (
                    <Slot
                      image={view?.assetId}
                      label={side}
                      filling={
                        filling === kind || (step === 'views' && toDraw.includes(kind)) || redrawing === kind
                      }
                      locked={!consent}
                      busy={busy || Boolean(redrawing)}
                      t={t}
                      onPick={() => pickFor(kind)}
                      onRedraw={face ? () => void redrawSide(kind) : undefined}
                      onRemove={() => void removeView(kind)}
                    />
                  ) : drawing ? (
                    <span className="spinner" />
                  ) : view ? (
                    <>
                      <img src={assetUrl(view.assetId)} alt={side} />
                      <span className="mx__acts">
                        <button
                          type="button"
                          aria-label={t('caster.redrawSide', { side })}
                          title={t('caster.redrawSide', { side })}
                          disabled={busy || Boolean(redrawing)}
                          onClick={() => void redrawSide(kind)}
                        >
                          ↻
                        </button>
                        <button
                          type="button"
                          aria-label={t('caster.removeView', { side })}
                          title={t('caster.removeView', { side })}
                          onClick={() => void removeView(kind)}
                        >
                          ×
                        </button>
                      </span>
                    </>
                  ) : null}
                </span>
                <figcaption>{side}</figcaption>
              </figure>
            );
          })}
        </div>

        <div className="mx__actions">
          {manual && (
            <input
              ref={slotRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="sr-only"
              tabIndex={-1}
              aria-hidden="true"
              data-testid="slot-file"
              onChange={(event) => void fillSlot(event.target.files?.[0])}
            />
          )}
          {manual && (
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy || !face}
              title={face ? undefined : t('caster.needFace')}
              onClick={() => void shoot360(effectiveIdentity, toDraw)}
            >
              {step ? <span className="spinner" /> : <SparkIcon />}
              {step ? t('caster.step.views') : t(whole ? 'caster.redo360Beige' : 'caster.make360Beige')}
            </button>
          )}
          {!manual && (
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy || (identityIsThin && !face)}
              onClick={() => void shoot360()}
            >
              {step ? <span className="spinner" /> : <SparkIcon />}
              {step
                ? t(`caster.step.${step}`)
                : t(!face ? 'caster.shoot360' : whole ? 'caster.redo360' : 'caster.make360')}
            </button>
          )}
          {editing && !manual && (
            <button type="button" className="btn" disabled={busy} onClick={() => void shootTakes()}>
              {takesBusy ? <span className="spinner" /> : null}
              {t(takesBusy ? 'caster.shooting' : 'caster.newTakes')}
            </button>
          )}
          {!manual && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                multiple
                className="sr-only"
                tabIndex={-1}
                aria-hidden="true"
                onChange={(event) => void uploadReferences(event.target.files)}
              />
              <button
                type="button"
                className="btn btn--ghost"
                disabled={busy || !consent}
                title={consent ? undefined : t('caster.consentFirst')}
                onClick={() => fileRef.current?.click()}
              >
                {reading ? <span className="spinner" /> : <PlusIcon />}
                {t(reading ? 'caster.reading' : 'caster.upload')}
              </button>
            </>
          )}
          {/*
           * The attestation, beside the import it is about: this is a
           * photograph of a real person, and the studio is about to render
           * them. The server refuses the upload without it.
           */}
          <label className="mx__consent">
            <input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} />
            <span>{t('caster.consent')}</span>
          </label>
        </div>
        {manual ? (
          <p className="faint mx__note">{t('caster.manualHint')}</p>
        ) : (
          identityIsThin && !face && <p className="faint mx__note">{t('caster.thinShort')}</p>
        )}
        {notes.length > 0 && <CaptureNotes notes={notes} onDismiss={() => setNotes([])} />}

        {referenceAssetIds.length > 1 && (
          <div className="mx__faces">
            {referenceAssetIds.map((id, index) => (
              <span key={id} className={`caster__thumb ${index === 0 ? 'is-primary' : ''}`}>
                <img
                  src={assetUrl(id)}
                  alt=""
                  role="button"
                  tabIndex={0}
                  title={t(index === 0 ? 'caster.isPrimary' : 'caster.makePrimary')}
                  onClick={() => index > 0 && void makePrimary(id)}
                  onKeyDown={(event) => {
                    if (index > 0 && (event.key === 'Enter' || event.key === ' ')) {
                      event.preventDefault();
                      void makePrimary(id);
                    }
                  }}
                />
                <button
                  type="button"
                  aria-label={t('caster.removePortrait')}
                  onClick={() => void discardTake(id)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        {candidates.length > 0 && (
          <section className="takes">
            <span className="label">{t('caster.takes')}</span>
            <div className="takes__strip">
              {candidates.map((assetId) => (
                <figure key={assetId} className="take">
                  <img src={assetUrl(assetId)} alt="" loading="lazy" />
                  <figcaption className="take__acts">
                    <button
                      type="button"
                      className="take__btn take__btn--keep"
                      aria-label={t('caster.keepAria')}
                      title={t('caster.keep')}
                      onClick={() => void keepTake(assetId)}
                    >
                      <KeepIcon />
                    </button>
                    <button
                      type="button"
                      className="take__btn take__btn--drop"
                      aria-label={t('caster.discardAria')}
                      title={t('caster.discard')}
                      onClick={() => void discardTake(assetId)}
                    >
                      <RejectIcon />
                    </button>
                  </figcaption>
                </figure>
              ))}
            </div>
          </section>
        )}

        <div className="field">
          <label htmlFor="model-name">{t('caster.name')}</label>
          <input
            id="model-name"
            className="input mx__name"
            value={name}
            placeholder="Ava"
            onChange={(event) => setName(event.target.value)}
          />
        </div>

        {/*
         * Everything that describes her, folded: the brief or an imported
         * photograph fills it, so it is opened to correct, not to fill.
         */}
        <details className="mx__fold">
          <summary>{t('caster.describe')}</summary>
          {/*
           * One list, read down: the name of each feature, and what she has,
           * chosen rather than typed — the words the renderer needs are
           * behind the choices, in English, while the choices read in the
           * interface's language. Only her origin and what sets her apart
           * are written, because no list covers them.
           */}
          <div className="desc">
            <DescSelect
              label={t('caster.gender')}
              value={appearance.gender}
              options={GENDERS.map((g) => ({ value: g, label: t(`enum.gender.${g}`) }))}
              onChange={(v) => set('gender', v as ModelAppearance['gender'])}
            />
            <DescSelect
              label={t('caster.build')}
              value={appearance.build}
              options={BUILDS.map((b) => ({ value: b, label: t(`enum.build.${b}`) }))}
              onChange={(v) => set('build', v as ModelAppearance['build'])}
            />
            {(
              [
                'ageRange',
                'height',
                'skinTone',
                'eyeColor',
                'hairColor',
                'hairLength',
                'hairStyle',
                'makeup',
              ] as const
            ).map((field) => (
              <DescSelect
                key={field}
                label={t(DESC_LABEL[field])}
                value={appearance[field]}
                options={choicesFor(field, appearance[field], locale)}
                blank={t('caster.unset')}
                onChange={(v) => set(field, v)}
              />
            ))}
            <DescText
              label={t('caster.ethnicity')}
              value={appearance.ethnicity}
              placeholder={t('caster.ethnicityPlaceholder')}
              onChange={(v) => set('ethnicity', v)}
            />
            <DescText
              label={t('caster.features')}
              value={appearance.distinguishingFeatures}
              placeholder={t('caster.featuresPlaceholder')}
              onChange={(v) => set('distinguishingFeatures', v)}
            />
          </div>
        </details>

        {/*
         * The line the renderer receives, verbatim and in English in any
         * locale — folded, because it is for the rare hand correction.
         */}
        <details className="mx__fold">
          <summary>{t('caster.identity')}</summary>
          <div className="field">
            <div className="row row--between">
              <label htmlFor="identity" className="sr-only">
                {t('caster.identity')}
              </label>
              {identityTouched && (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => setIdentityTouched(false)}
                >
                  {t('caster.rebuild')}
                </button>
              )}
            </div>
            <textarea
              id="identity"
              lang="en"
              className="textarea"
              rows={2}
              value={effectiveIdentity}
              onChange={(event) => {
                setIdentityTouched(true);
                setIdentityPrompt(event.target.value);
              }}
            />
          </div>
        </details>
      </div>
    </Sheet>
  );
}

/**
 * One photograph slot of a model made by hand: empty, a button that asks for
 * the photograph; filled, the photograph, to replace or take out.
 */
function Slot({
  image,
  label,
  filling,
  locked,
  busy,
  t,
  onPick,
  onRedraw,
  onRemove,
}: {
  image?: string;
  label: string;
  filling: boolean;
  locked: boolean;
  busy: boolean;
  t: Translate;
  onPick: () => void;
  /** Drawn again from her face, when there is one to draw from. */
  onRedraw?: () => void;
  onRemove: () => void;
}) {
  if (filling) return <span className="spinner" />;
  const hint = locked ? t('caster.consentFirst') : undefined;
  if (!image) {
    return (
      <button
        type="button"
        className="mx__slot"
        disabled={busy || locked}
        title={hint}
        aria-label={t('caster.addSlot', { side: label })}
        onClick={onPick}
      >
        <PlusIcon />
        <span>{t('caster.addPhoto')}</span>
      </button>
    );
  }
  return (
    <>
      <img src={assetUrl(image)} alt={label} />
      <span className="mx__acts">
        {onRedraw && (
          <button
            type="button"
            aria-label={t('caster.redrawSide', { side: label })}
            title={t('caster.redrawSide', { side: label })}
            disabled={busy}
            onClick={onRedraw}
          >
            ↻
          </button>
        )}
        <button
          type="button"
          aria-label={t('caster.replaceSlot', { side: label })}
          title={hint ?? t('caster.replaceSlot', { side: label })}
          disabled={busy || locked}
          onClick={onPick}
        >
          ↥
        </button>
        <button
          type="button"
          aria-label={t('caster.removeView', { side: label })}
          title={t('caster.removeView', { side: label })}
          disabled={busy}
          onClick={onRemove}
        >
          ×
        </button>
      </span>
    </>
  );
}

/** Which caption each chosen feature is listed under. */
const DESC_LABEL = {
  ageRange: 'caster.age',
  height: 'caster.height',
  skinTone: 'caster.skinTone',
  eyeColor: 'caster.eyeColour',
  hairColor: 'caster.hairColour',
  hairLength: 'caster.hairLength',
  hairStyle: 'caster.hairStyle',
  makeup: 'caster.makeup',
} as const;

/** A feature chosen from a list: its name on the left, a pill on the right. */
function DescSelect({
  label,
  value,
  options,
  blank,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  /** The "not said" choice, for a feature that may be left to the renderer. */
  blank?: string;
  onChange: (value: string) => void;
}) {
  const id = useId();
  return (
    <div className="desc__row">
      <label htmlFor={id}>{label}</label>
      <select
        id={id}
        className={`desc__pill ${value ? '' : 'is-blank'}`}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {blank !== undefined && <option value="">{blank}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/** A feature written in words, where no list could cover it. */
function DescText({
  label,
  value,
  placeholder,
  onChange,
}: {
  label: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}) {
  const id = useId();
  return (
    <div className="desc__row desc__row--text">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        className="desc__pill desc__input"
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}
